import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, PlayCircle, Users, Star, Award } from 'lucide-react';
import './ViewCourse.css';
import { useLearningsStore } from '@/stores/LearnerStore/LearningsStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';

export default function ViewCoursePage() {
  const location = useLocation();
  const sourceLabel = (location.state as any)?.sourceLabel || 'My Courses';
  const [expandedSection, setExpandedSection] = useState<string | null>('introduction');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const {
    courseDetails,
    loadingCourseDetails,
    errorCourseDetails,
    fetchCourseDetails,
  } = useLearningsStore();

  // Fetch course details on mount (using courseId 1 as example)
  useEffect(() => {
    fetchCourseDetails(1);
  }, [fetchCourseDetails]);

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const toggleFaq = (faqId: number) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  // Loading state
  if (loadingCourseDetails) {
    return (
      <LoadingSpinner
        fullScreen
        size="lg"
      />
    );
  }

  // Error state
  if (errorCourseDetails) {
    return (
      <ErrorDisplay
        fullScreen
        title="Failed to Load Course Details"
        message={errorCourseDetails}
        type="error"
        showRetry
        onRetry={() => fetchCourseDetails(1)}
      />
    );
  }

  // No data state
  if (!courseDetails) {
    return (
      <ErrorDisplay
        fullScreen
        title="Course Not Found"
        message="The course you're looking for doesn't exist."
        type="error"
      />
    );
  }

  return (
    <div className="view-course-container">
      {/* Header */}
      <header className="view-course-header">
        <div className="view-course-header__content">
          <div className="view-course-breadcrumb">
            <span className="view-course-breadcrumb-item">{sourceLabel}</span>
            <span className="view-course-breadcrumb-separator"> › </span>
            <span className="view-course-breadcrumb-item view-course-breadcrumb-active">
              {courseDetails?.title || 'Course Details'}
            </span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="view-course-hero">
        <div className="view-course-hero__content">
          <div className="view-course-hero__text">
            <h1 className="view-course-hero__title">{courseDetails.title}</h1>
            <p className="view-course-hero__subtitle">
              {courseDetails.subtitle}
            </p>
            <div className="view-course-hero__stats">
              <div className="view-course-hero__stat">
                <Star size={16} color="#f59e0b" fill="#f59e0b" />
                <span className="view-course-hero__stat-text">{courseDetails.rating}</span>
                <span className="view-course-hero__stat-label">{courseDetails.totalRatings} Ratings</span>
              </div>
              <div className="view-course-hero__stat">
                <Users size={16} color="#8b5cf6" />
                <span className="view-course-hero__stat-text">{courseDetails.totalStudents.toLocaleString()} Students</span>
              </div>
            </div>
            <div className="view-course-hero__instructor">
              <div className="view-course-hero__instructor-avatar">{courseDetails.instructor.avatar}</div>
              <span className="view-course-hero__instructor-name">{courseDetails.instructor.name}</span>
            </div>
          </div>
          <div className="view-course-hero__image">
            <div className="view-course-hero__image-placeholder">
              <div className="view-course-hero__mock-image"></div>
            </div>
            <button className="view-course-hero__wishlist-btn">Add to Wishlist</button>
            {!courseDetails?.is_enrolled && (
              <button className="view-course-hero__cta-btn">Enroll Now</button>
            )}
          </div>
        </div>
      </section>

      {/* Course Info Cards */}
      <div className="view-course-info-cards">
        <div className="view-course-info-card">
          <h3 className="view-course-info-card__title">This course includes:</h3>
          <ul className="view-course-info-list">
            {courseDetails.courseIncludes.map((item, index) => (
              <li key={index} className="view-course-info-item">
                {index < 3 ? <PlayCircle size={16} color="#8b5cf6" /> : <Award size={16} color="#8b5cf6" />}
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Main Content */}
      <main className="view-course-main">
        {/* What You'll Learn */}
        <section className="view-course-section">
          <h2 className="view-course-section__title">What you'll learn</h2>
          <div className="view-course-learning-grid">
            {courseDetails.learningPoints.map((point, index) => (
              <div key={index} className="view-course-learning-item">
                <span className="view-course-learning-checkmark">✓</span>
                <span className="view-course-learning-text">{point}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Course Content */}
        <section className="view-course-section">
          <h2 className="view-course-section__title">Course content</h2>
          <p className="view-course-content-stats">
            {courseDetails.contentStats.sections} Sections • {courseDetails.contentStats.lectures} Lectures • {courseDetails.contentStats.totalDuration} total length
          </p>
          <div className="view-course-modules">
            {courseDetails.courseModules.map((module) => (
              <div key={module.id} className="view-course-module">
                <div className="view-course-module__header" onClick={() => toggleSection(module.id)}>
                  <div className="view-course-module__header-left">
                    <ChevronDown
                      size={20}
                      style={{
                        transform: expandedSection === module.id ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                    <span className="view-course-module__title">{module.title}</span>
                  </div>
                  <div className="view-course-module__stats">
                    <span>{module.lectures} Lectures</span>
                    <span>{module.duration}</span>
                  </div>
                </div>
                {expandedSection === module.id && module.lessons.length > 0 && (
                  <div className="view-course-module__content">
                    {module.lessons.map((lesson, index) => (
                      <div key={index} className="view-course-video-item">
                        <div className="view-course-video-item__left">
                          <PlayCircle size={16} color="#6b7280" />
                          <span className="view-course-video-item__title">{lesson.title}</span>
                        </div>
                        <span className="view-course-video-item__duration">{lesson.duration}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Instructor */}
        <section className="view-course-section">
          <h2 className="view-course-section__title">Instructor</h2>
          <div className="view-course-instructor-card">
            <h3 className="view-course-instructor-card__name">{courseDetails.instructor.name}</h3>
            <p className="view-course-instructor-card__title">{courseDetails.instructor.title}</p>
            <div className="view-course-instructor-card__avatar">{courseDetails.instructor.avatar}</div>
            <div className="view-course-instructor-card__stats">
              <div className="view-course-instructor-card__stat-item">
                <Star size={16} color="#f59e0b" />
                <span>{courseDetails.instructor.rating} {courseDetails.instructor.totalRatings.toLocaleString()} Ratings</span>
              </div>
              <div className="view-course-instructor-card__stat-item">
                <Users size={16} color="#6b7280" />
                <span>{courseDetails.instructor.totalStudents.toLocaleString()} Students</span>
              </div>
              <div className="view-course-instructor-card__stat-item">
                <PlayCircle size={16} color="#6b7280" />
                <span>{courseDetails.instructor.totalCourses} Courses</span>
              </div>
            </div>
            {courseDetails.instructor.bio.map((paragraph, index) => (
              <p key={index} className="view-course-instructor-card__bio">
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        {/* Reviews */}
        <section className="view-course-section">
          <h2 className="view-course-section__title">
            <Star size={24} color="#f59e0b" fill="#f59e0b" />
            {courseDetails.rating} Course Ratings • {courseDetails.totalRatings} Ratings
          </h2>
          <div className="view-course-reviews">
            {courseDetails.reviews.map((review) => (
              <div key={review.id} className="view-course-review">
                <div className="view-course-review__header">
                  <div className="view-course-review__avatar">{review.avatar}</div>
                  <div className="view-course-review__header-text">
                    <span className="view-course-review__name">{review.name}</span>
                    <span className="view-course-review__time">{review.time}</span>
                  </div>
                </div>
                <div className="view-course-review__stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} size={14} color="#f59e0b" fill={star <= review.rating ? "#f59e0b" : "none"} />
                  ))}
                </div>
                <p className="view-course-review__text">
                  {review.comment}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="view-course-section">
          <h2 className="view-course-section__title">FAQ</h2>
          <div className="view-course-faq-list">
            {courseDetails.faqs.map((faq) => (
              <div key={faq.id} className="view-course-faq-item">
                <div
                  className={`view-course-faq-question ${
                    faq.id === 2 ? 'view-course-faq-question--highlight' : ''
                  }`}
                  onClick={() => toggleFaq(faq.id)}
                >
                  <span className="view-course-faq-number">Q{faq.id}</span>
                  <span className="view-course-faq-question-text">{faq.question}</span>
                  <ChevronDown
                    size={20}
                    style={{
                      transform: expandedFaq === faq.id ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </div>
                {expandedFaq === faq.id && <div className="view-course-faq-answer">{faq.answer}</div>}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
